/**
 * Display-only formatters for match score breakdown (card tooltip).
 * The scoring engine stores full-precision `earned` values; use these helpers only when rendering.
 */

/**
 * Integer match percentage for UI and for {@link computeCDMOMatchScore}'s `percentageScore`:
 * if the fractional part is **strictly greater than** 0.5, round up (ceiling); otherwise use the
 * integer part (floor). Examples: 87.48 → 87, 87.51 → 88, 87.5 → 87.
 *
 * @param n - Raw percentage in 0–100 (may be fractional)
 * @returns Integer in [0, 100]
 */
export function formatMatchPercentageForDisplay(n: number): number {
  if (!Number.isFinite(n)) return 0;
  const x = Math.min(Math.max(Number(n), 0), 100);
  const base = Math.trunc(x);
  const frac = x - base;
  const out = frac > 0.5 ? Math.ceil(x) : base;
  return Math.min(Math.max(out, 0), 100);
}

/**
 * Formats earned or max points for the match breakdown tooltip: one decimal place via `toFixed(1)`.
 *
 * @param n - Raw `earned` or `max` from the scoring engine breakdown
 * @returns String for UI, e.g. `"25.0"`, `"12.5"`, `"1.7"`
 */
export function formatMatchBreakdownPointsForDisplay(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return Number(n).toFixed(1);
}
